import React, { useEffect, useState, useRef } from "react";
import { BsChevronDown, BsChevronUp } from "react-icons/bs";
import { AiOutlineCheck, AiOutlineWarning, AiFillFileAdd } from "react-icons/ai";
import CreateChartNotesModal from "../../../Common/popUpModals/CreateChartNotesModal";
import { replaceNextLineCharWithNextLineTag } from "../../../../util/common";
import ReactHtmlParser from "react-html-parser";
import { getNpiFromName } from "../../../../util/npiLookup";
import { buildChartNotePdf } from "../../../../util/chartNotePdf";
import html2canvas from "html2canvas";

const getHighlightedText = (text, highlight) => {
  const parts = text.split(new RegExp(`(${highlight})`, "gi"));
  return (
    <span>
      {parts.map((part, i) =>
        part.toLowerCase() === highlight.toLowerCase() ? (
          <span key={i} className="highlight">
            {part}
          </span>
        ) : (
          part
        )
      )}
    </span>
  );
};

export default function SingleChart({
  search,
  chartStatus,
  referenceId,
  isClinicalRequired,
  createdDate,
  chartDetails,
  authorName,
  noteMessage,
  patientDetail,
  cb,
  isExpanded: isExpandedProp
}) {
  // Ensure isExpanded is always a boolean
  const [isExpanded, setIsExpanded] = useState(Boolean(isExpandedProp));
  const [loading, setLoading] = useState(false);
  const [resultData, setResultData] = useState(null);
  const [showCreateChartNotesModal, setShowCreateChartNotesModal] = useState(false);
  const contentRef = useRef(null);
  const [isClone, setIsClone] = useState(false);

  const onClickChevronDown = async () => {
    try {
      setIsExpanded(true);
    } catch (error) {
      setLoading(false);
    }
  };
  const searchInNotes = () => {
    if (noteMessage && search && noteMessage.toLowerCase().includes(search.toLowerCase())) {
      return true;
    } else {
      return false;
    }
  };
  const onClickCloneNote = () => {
    setIsClone(true);
    setShowCreateChartNotesModal(true);
  };

  const handleDownloadPdf = async () => {
    try {
      const arraybuffer = await buildChartNotePdf(patientDetail, chartDetails);
      const blob = new Blob([arraybuffer], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${patientDetail?.name || "chart"}_note.pdf`;
      a.click();
      // Defer revoke so the browser can capture the blob URL before it is invalidated
      setTimeout(() => URL.revokeObjectURL(url), 100);
    } catch (err) {
      console.error("PDF generation failed", err);
    }
  };

  // If search changes, expand if search matches, but don't collapse or override external expansion control otherwise
  useEffect(() => {
    if (!search?.length) return;

    const result = searchInNotes();

    // Only override expansion if match is found
    if (result) {
      setIsExpanded(true);
    }
    // If no match, don’t collapse manually — let external control apply
  }, [search, noteMessage]);

  // Respond to changes in isExpandedProp, always using Boolean for safety
  useEffect(() => {
    setIsExpanded(Boolean(isExpandedProp));
  }, [isExpandedProp]);

  // Helper to normalize a string: lowercase, remove non-letters, trim
  const normalize = str =>
    str
      .toLowerCase()
      .replace(/[^a-z\s]/gi, "")
      .replace(/\s+/g, " ")
      .trim();

  // Check if the normalized noteType contains "beluga"
  const isBeluga = chartDetails?.noteType && normalize(chartDetails.noteType).includes("beluga");

  return (
    <div className={`chartingNoteContent ${isExpanded ? "expanded" : ""}`}>
      <CreateChartNotesModal
        show={showCreateChartNotesModal}
        onHide={() => setShowCreateChartNotesModal(false)}
        patientDetails={patientDetail}
        referenceId={referenceId}
        referenceType={chartDetails?.referenceType}
        visibility={chartDetails?.visibility}
        personId={chartDetails?.personId}
        chartDetails={chartDetails}
        cb={cb}
        isClone={isClone}
      />
      <div>
        <p>NPI</p>
        <h5>{getNpiFromName(authorName, chartDetails?.noteType)}</h5>
      </div>
      <div
        className="borderContent-left"
        style={{
          width: "100%"
        }}
      >
        {isBeluga && referenceId ? (
          <>
            <p>Master ID</p>
            <h5>{referenceId}</h5>
          </>
        ) : chartDetails?.referenceType === "BOOKING" && referenceId ? (
          <>
            <p>Booking ID</p>
            <h5>{referenceId}</h5>
          </>
        ) : referenceId ? (
          <>
            <p>Kit ID</p>
            <h5>{referenceId}</h5>
          </>
        ) : null}
      </div>
      <div className="borderContent-kit">
        <p>{isBeluga ? "Provider Name" : "Author"}</p>
        <h5>
          <span>
            <AiOutlineCheck color="green" />
          </span>{" "}
          {authorName}
        </h5>
      </div>
      <div>
        <p>Created</p>
        <h5>{createdDate}</h5>
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          alignItems: "flex-start"
        }}
      >
        <div style={{ display: "flex", justifyContent: "center", alignItems: "flex-start", flexDirection: "column" }}>
          {chartDetails?.noteType === "PATIENT_QUESTIONNAIRE_RESPONSE" && (
            <div
              style={{
                margin: "0.5px 0"
              }}
            >
              <button className="viewDetailsBtn" onClick={onClickCloneNote}>
                <AiFillFileAdd
                  style={{
                    fontSize: "1.5rem",
                    color: "#007F61",
                    cursor: "pointer"
                  }}
                />
                <span className="chartingColour">Clone Note</span>
              </button>
            </div>
          )}
          {isExpanded ? (
            <div style={{ display: "flex", gap: "10px" }}>
              <button className="viewDetailsBtn" onClick={handleDownloadPdf}>
                <AiOutlineCheck
                  style={{
                    fontSize: "1.5rem",
                    color: "#007F61",
                    cursor: "pointer"
                  }}
                />
                <span className="chartingColour">Download PDF</span>
              </button>
              <button className="viewDetailsBtn" onClick={() => setIsExpanded(false)}>
                <BsChevronUp
                  style={{
                    fontSize: "1.5rem",
                    color: "#007F61",
                    cursor: "pointer"
                  }}
                />
                <span className="chartingColour">Close Notes</span>
              </button>
            </div>
          ) : (
            <button className="viewDetailsBtn" onClick={onClickChevronDown}>
              <BsChevronDown
                style={{
                  fontSize: "1.5rem",
                  color: "#007F61",
                  cursor: "pointer"
                }}
              />
              <span className="chartingColour"> View Notes</span>
            </button>
          )}
        </div>
      </div>
      <div className={`${resultData ? "resultData" : "gridSpanFullCol"}`}>
        <div className="resultData">
          {noteMessage ? (
            <div className="resultContainer">
              <p>Note</p>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between"
                }}
              >
                {search ? (
                  getHighlightedText(noteMessage, search)
                ) : (
                  <div className="reset-div" dangerouslySetInnerHTML={{ __html: noteMessage }}></div>
                )}
              </div>
            </div>
          ) : (
            <div
              style={{
                gridColumn: "1 / span 7",
                display: "flex",
                justifyContent: "center",
                alignItems: "center"
              }}
            >
              <h1 className="noRecordFound">No Message Found</h1>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
